import type { CityConfig } from "../hawaii-city/types";

export const kiheiConfig: CityConfig = {
  city: "Kihei",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "kihei",
  route: "/cities/kihei",
  title: "Emergency & Expedited Passport Services in Kihei, HI — South Maui | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Kihei, HI. Fast passport renewal and urgent passport help for South Maui, Wailea, and Makena travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Kihei, HI",
  heroSubtitle: "Kihei, Wailea, and South Maui residents and visitors — get your passport fast without leaving the island. Start online, ship overnight, and we handle the rest. As fast as 24 hours.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Stunning%20Kihei%20Maui%20Hawaii%20beach%20at%20sunset%2C%20golden%20sand%20with%20palm%20trees%2C%20calm%20turquoise%20ocean%20waters%2C%20warm%20tropical%20lighting%2C%20distant%20Molokini%20crater%20visible%2C%20professional%20travel%20photography%2C%20vibrant%20warm%20colors%20with%20deep%20blue%20ocean%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=kihei-hero-01&orientation=landscape",
  heroImageAlt: "Kihei Maui beach at sunset with turquoise ocean and palm trees",
  geoPosition: "20.7644;-156.4500",
  geoPlacename: "Kihei, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "Inter-island flight to Honolulu — about 100 miles",
  },
  nearbyCitiesZips: [
    { name: "Kihei", zips: "96753" },
    { name: "Wailea", zips: "96753" },
    { name: "Makena", zips: "96753" },
    { name: "Kahului", zips: "96732" },
    { name: "Wailuku", zips: "96793" },
    { name: "Lahaina", zips: "96761" },
    { name: "Paia", zips: "96779" },
    { name: "Makawao", zips: "96768" },
    { name: "Kula", zips: "96790" },
    { name: "Kaanapali", zips: "96761" },
    { name: "Napili", zips: "96761" },
    { name: "Honolulu", zips: "96815" },
    { name: "Kailua", zips: "96734" },
    { name: "Hilo", zips: "96720" },
    { name: "Kailua-Kona", zips: "96740" },
    { name: "Kahului Airport", zips: "96732" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Kihei home or resort in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping from Kihei or Wailea to send your documents directly to us." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots — no public appointment needed for South Maui residents." },
  ],
  testimonials: [
    {
      name: "Amanda S.",
      location: "Kihei, HI",
      rating: 5,
      text: "I had a business trip to Vancouver in 6 days and my passport was expired. Fast Passport Center processed my renewal from Kihei in 5 days. The shipping from South Maui was seamless and their team was incredibly responsive.",
      service: "Passport Renewal",
    },
    {
      name: "Chris D.",
      location: "Wailea, HI",
      rating: 5,
      text: "We were vacationing in Wailea and realized my wife's passport was expired 2 weeks before our European cruise. FPC handled everything remotely and had a new passport shipped to our resort with time to spare.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Patricia L.",
      location: "Kahului, HI",
      rating: 5,
      text: "Emergency trip to New Zealand for a family matter. I'm in Kahului but work in Kihei. FPC handled everything and the passport arrived in 5 days. No need to fly to Honolulu for an agency appointment.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "kihei-usps",
      name: "Kihei Post Office",
      type: "USPS Passport Acceptance",
      address: "1250 S Kihei Rd",
      city: "Kihei",
      state: "HI",
      zip: "96753",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Kihei",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Appointment required. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "kahului-usps-kihei",
      name: "Kahului Post Office",
      type: "USPS Passport Acceptance",
      address: "138 Puunene Ave",
      city: "Kahului",
      state: "HI",
      zip: "96732",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 15 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Central Maui option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "lahaina-usps-kihei",
      name: "Lahaina Post Office",
      type: "USPS Passport Acceptance",
      address: "1760 Honoapiilani Hwy",
      city: "Lahaina",
      state: "HI",
      zip: "96761",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "West Maui — about 25 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "West Maui option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "wailuku-usps-kihei",
      name: "Wailuku Post Office",
      type: "USPS Passport Acceptance",
      address: "250 Imi Kala St",
      city: "Wailuku",
      state: "HI",
      zip: "96793",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 18 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Wailuku area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Kihei, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours. Standard processing through a Kihei or Kahului post office can take 2–6 months. For South Maui residents, our courier service is the fastest option without flying to Honolulu.",
    },
    {
      q: "Do I need to fly to Honolulu for a passport?",
      a: "No. Most Kihei and South Maui travelers start online and ship documents via overnight courier. The Kihei, Kahului, or Wailuku Post Offices can serve as acceptance facilities if needed, but you don't need to visit the Honolulu agency.",
    },
    {
      q: "Where is the closest passport acceptance facility to Kihei?",
      a: "The Kihei Post Office at 1250 S Kihei Rd accepts passport applications by appointment. Kahului Post Office and Wailuku Post Office are also options for Central Maui residents.",
    },
    {
      q: "What if I'm a tourist in Wailea or Kihei and need a passport emergency?",
      a: "We frequently help tourists and vacationers in Kihei, Wailea, and Makena who discover passport issues while traveling. We can ship to your resort or hotel address and coordinate return shipping so you don't miss your flight.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants require an in-person acceptance clerk visit. The Kihei or Kahului Post Office are the most convenient options for South Maui residents. We guide you through exactly what to bring.",
    },
    {
      q: "How does shipping from Kihei work?",
      a: "You can ship overnight using FedEx or UPS from Kihei or Wailea. Most South Maui residents use the FedEx location near Kihei Town Center or the UPS Store in Kahului.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if I need a child passport in Kihei?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions for Kihei and Wailea families.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64). We guide South Maui residents through the exact process, including the acceptance clerk visit and expedited submission.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Kihei", address: "1215 S Kihei Rd, Kihei, HI 96753", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Kahului", address: "45 Kamehameha Ave, Kahului, HI 96732", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Kihei", address: "247 Piikea Ave, Kihei, HI 96753", hours: "Daily 7 AM – 10 PM" },
    { name: "The UPS Store — Kihei", address: "1819 S Kihei Rd, Kihei, HI 96753", hours: "Call for hours" },
    { name: "Walmart — Kahului", address: "101 Pakaula St, Kahului, HI 96732", hours: "Call for hours" },
  ],
  dmvLocations: [
    {
      name: "Kihei DMV — Kihei Satellite City Hall",
      address: "1215 S Kihei Rd, Kihei, HI 96753",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kahului DMV — Maui County Service Center",
      address: "70 E Kaahumanu Ave, Kahului, HI 96732",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Central Maui option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "OGG",
      name: "Kahului Airport",
      distance: "About 15 miles north — Maui",
      address: "1 Keolani Pl, Kahului, HI 96732",
      note: "Major airport for Maui with direct flights to mainland U.S., Canada, and inter-island connections. Most South Maui travelers fly from OGG for international trips.",
      flightUrl: "https://www.hawaii.gov/ogg/",
      flightLabel: "OGG Airport Website",
      featured: true,
    },
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "Inter-island flight — Honolulu",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub. Many Maui travelers connect through HNL for international destinations.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Kihei and South Maui.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help South Maui travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide South Maui residents through every step.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions for Kihei and Wailea families.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports for South Maui residents.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Kihei and South Maui travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for South Maui residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for South Maui residents with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for South Maui residents with lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Kihei and Wailea, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service for South Maui residents after marriage, divorce, or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent South Maui travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many South Maui travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option from Kihei or Wailea." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to fly to Honolulu." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–2", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — fly to Honolulu", fpc: "Most travelers ship documents overnight from Kihei", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Kahului", route: "/cities/kahului" },
    { name: "Lahaina", route: "/cities/lahaina" },
    { name: "Honolulu", route: "/cities/honolulu" },
    { name: "Kailua", route: "/cities/kailua" },
    { name: "Hilo", route: "/cities/hilo" },
    { name: "Kailua-Kona", route: "/cities/kailua-kona" },
  ],
  fpcOfficeAddress: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your Kihei or Wailea home, ship with our prepaid label from any UPS/FedEx location in Kihei or Kahului, or schedule a drop-off. Most South Maui clients ship overnight.",
  fpcOfficeDistance: "Serving Kihei and all of South Maui remotely",
  serviceAreaIntro: "Whether you are in Kihei, Wailea, Makena, or anywhere on South Maui, Fast Passport Center makes expedited passport processing simple — no flying to Honolulu, no agency lines.",
  nearbyCitiesIntro: "Anywhere on South Maui — start online, ship overnight from Kihei or Wailea, and we handle the rest.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Kihei as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for South Maui travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Kihei, Wailea, and Kahului who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything South Maui travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Maui",
  islandNickname: "The Valley Isle",
  landmarkCards: [
    {
      name: "Kihei Coastline",
      description: "Kihei's 6-mile stretch of South Maui beaches — Kamaole I, II, and III — draws travelers from across the Pacific. The sunny, low-rainfall microclimate makes Kihei one of Maui's most desirable addresses.",
      imageUrl: "https://readdy.ai/api/search-image?query=Kamaole%20Beach%20Kihei%20South%20Maui%20Hawaii%2C%20golden%20sand%20beach%20with%20palm%20trees%2C%20turquoise%20Pacific%20Ocean%2C%20gentle%20waves%2C%20bright%20sunny%20tropical%20day%2C%20professional%20travel%20photography%2C%20vibrant%20colors%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kihei-kamaole&orientation=landscape",
    },
    {
      name: "Molokini Crater",
      description: "A crescent-shaped volcanic crater just 2.5 miles off Kihei's coastline, Molokini is one of the world's top snorkel and dive sites. Tour boats depart daily from nearby Ma'alaea Harbor.",
      imageUrl: "https://readdy.ai/api/search-image?query=Molokini%20crater%20Maui%20Hawaii%20aerial%20view%2C%20crescent%20shaped%20volcanic%20island%20in%20turquoise%20ocean%2C%20snorkeling%20boats%20nearby%2C%20clear%20water%20visible%20coral%20reef%20below%2C%20professional%20travel%20photography%2C%20stunning%20Pacific%20scenery%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kihei-molokini&orientation=landscape",
    },
    {
      name: "Wailea Resort Area",
      description: "Kihei's upscale neighbor Wailea is home to Hawaii's finest luxury resorts — Four Seasons, Grand Wailea, and Fairmont Kea Lani. International travelers here frequently need urgent passport assistance.",
      imageUrl: "https://readdy.ai/api/search-image?query=Wailea%20beach%20resort%20Maui%20Hawaii%2C%20luxury%20resort%20coastline%20with%20pristine%20beach%2C%20turquoise%20ocean%2C%20tropical%20landscaping%20and%20palm%20trees%2C%20golden%20hour%20sunlight%2C%20professional%20travel%20photography%2C%20upscale%20tropical%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kihei-wailea&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-hotel-line", title: "Luxury Resort Guests", description: "Travelers staying at Wailea or Kihei resorts who discover expired passports can ship documents from Kihei UPS or FedEx and receive urgent processing. We ship returns to your resort." },
    { icon: "ri-flight-takeoff-line", title: "OGG via North Kihei Road", description: "Kihei residents reach Kahului Airport (OGG) in about 15 minutes via North Kihei Road. International routes include direct flights to Japan, Canada, and Australia." },
    { icon: "ri-ship-line", title: "Ma'alaea Harbor Cruises", description: "The snorkel cruise boats departing from nearby Ma'alaea sometimes go to Molokini only, but inter-island ferry and international cruises require valid passports." },
    { icon: "ri-truck-line", title: "Ship from South Maui", description: "The UPS Store in Kihei Town Center and FedEx near the Piilani Village area serve South Maui residents. Overnight shipping cuts are around 6pm daily." },
    { icon: "ri-sun-line", title: "South Maui Sun & Travel Culture", description: "South Maui's sunny, active lifestyle attracts international surfers, divers, and outdoor adventurers. Passport validity is an ongoing priority for Kihei's active travel community." },
    { icon: "ri-global-line", title: "Asian & Australian Visitors", description: "Kihei's beach community attracts significant numbers of Japanese, Australian, and Canadian visitors. Many become Kihei residents — and retain international travel ties that require current passports." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (HI DOH)", url: "https://health.hawaii.gov/vitalrecords/", desc: "Hawaii Department of Health — Office of Health Status Monitoring for birth and death certificates statewide" },
    { label: "Marriage Certificates (HI DOH)", url: "https://health.hawaii.gov/vitalrecords/marriage-certificates/", desc: "Certified copies of Hawaii marriage certificates from the Department of Health Vital Records Office" },
    { label: "Divorce Decrees (HI Family Court)", url: "https://www.courts.state.hi.us/self-help/court-forms/court-forms-family-court", desc: "Hawaii State Judiciary Family Court — divorce decree records by circuit" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Hawaii vital records online via VitalChek — fastest state-approved method" },
  ],
  dmvWebsite: "https://www.hawaii.gov/dot/highways/mvm",
  dmvLabel: "Hawaii DMV — Official Website",
};